import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcJ = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990 Schedule J: A Guide to Compensation Reporting"
          description="Understand and file Form 990 Schedule J with ease. Learn who needs to file, what information is required, and step-by-step filing instructions."
          keywords=""
          canonical="resources/tax-exempt/form-990-schedule-j/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule J</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Complete Compensation Information Reporting with TaxZerone!
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-j/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule J at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <b>Purpose: Form 990 Schedule J </b> reports
                        compensation, benefits, and compensation practices for
                        certain{' '}
                        <b>officers, directors, trustees, key employees,</b> and
                        <b> highest compensated employees.</b>
                      </span>
                    </li>

                    <li>
                      <span>
                        <b>Who Must File?</b> Organizations that answer{' '}
                        <b>"Yes"</b> to <b>Form 990, Part IV, line 23 </b>
                        generally must complete and attach <b>Schedule J.</b>
                      </span>
                    </li>

                    <li>
                      <span>
                        <b>What Is Reported?</b> Schedule J reports{' '}
                        <b>
                          salary, bonuses, deferred compensation, retirement
                          benefits, nontaxable benefits,{' '}
                        </b>
                        and compensation from related organizations.
                      </span>
                    </li>

                    <li>
                      <span>
                        <b>Executive Compensation Practices:</b> Reports certain
                        executive benefits, including{' '}
                        <b>
                          first-class travel, housing allowances, discretionary
                          spending accounts,
                        </b>{' '}
                        and other applicable compensation practices.
                      </span>
                    </li>

                    <li>
                      <span>
                        <b>Who Is Reported?</b> Only individuals who meet the{' '}
                        <b>IRS reporting requirements </b>are included. Not
                        every officer, director, trustee, or employee listed on
                        <b> Form 990</b> is reported.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule J?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule J?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#scha" sx={{ color: '#0202ff' }}>
                          Schedule J Filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule J filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule J?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>Schedule J, </b>
                  serves as a supplement to Form 990, providing a detailed
                  breakdown of compensation offered to specific individuals
                  within the organization, and information on certain
                  compensation practices of the organization. The individual
                  includes officers, directors, trustees, key employees, and
                  other highly compensated staff.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule J?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  An organization that answered “Yes” on{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 990
                  </Link>
                  , Part IV, Checklist of Required Schedules line 23, must
                  complete and attach Schedule J along with its 990 return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If an organization isn&apos;t required to{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 990
                  </Link>{' '}
                  but chooses to do so, it must file a complete return and
                  provide all of the information requested, including the
                  required schedules.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note</b> - Do not file Schedule J for institutional
                  trustees.
                </Alert>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <b>
                      Ready to attach Schedule J along with your Form 990 return
                      with TaxZerone?
                    </b>
                    <br />
                    Make the e-filing process simple by clicking the button
                    below.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schj`}
                  >
                    E-file Now
                  </Button>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="scha"
                >
                  Schedule J Filing Requirements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schj-details-img.webp" />
                  <source srcSet="/assets/images/f990-schj-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schj-details-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule J showing organization details and basic information"
                    width={760}
                    height={129}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  All tax-exempt organizations with certain Officers, Directors,
                  Trustees, Key Employees, and Highest Compensated Employees
                  must complete and attach Schedule J
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Schedule J filing requirements for
                  each part.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I - Questions Regarding Compensation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schj-part1-img.webp" />
                  <source srcSet="/assets/images/f990-schj-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schj-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule J, Part I, lines 1–3, questions about compensation policies, approval process, and use of independent review."
                    width={758}
                    height={374}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” for any box if the organization gives extra
                  benefits or personal-type perks to people listed in Form 990,
                  Part VII (officers, directors, key employees, etc.). Even if
                  these benefits were already reported as pay on tax forms, they
                  still must be checked here.
                </Typography>
                <Typography variant="body1">
                  After checking the boxes, explain the details in Part III,
                  including:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>What benefit was given,</li>
                  <li>Who received it (or how many people received it), and</li>
                  <li>Whether it was treated as taxable income.</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check First-class travel, Charter travel, Travel for
                  companions, Tax payments (gross-ups), Discretionary spending
                  account , Housing or residence, Use of personal home, Club
                  dues, Personal services
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1b
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    If any of these benefits were provided, answer “Yes” if the
                    organization followed a written policy for approving or
                    reimbursing them.
                  </li>
                  <li>
                    If there was no written policy, explain in Part III who
                    approved the benefits and how the decision was made.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if the organization requires receipts or proper
                  documentation before reimbursing any benefits or expenses
                  listed in line 1a for officers, directors, or the top
                  management official.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the box(es) showing how the top management official’s
                  pay was decided, such as: Compensation committee review,
                  Outside compensation consultant advice, Comparison with other
                  organizations Form 990s, Written employment contract Salary
                  survey or study, Final approval by the board or compensation
                  committee .
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schj-part1-line4-img.webp" />
                  <source srcSet="/assets/images/f990-schj-part1-line4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schj-part1-line4-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule J, Part I, lines 4-6, questions about compensation policies, approval process, and use of independent review."
                    width={760}
                    height={337}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In Part III, list the names of listed persons (officers,
                  directors, key employees, etc.) who received payments under
                  the arrangements in lines 4a–4c, the amounts paid, and explain
                  the terms ofthose arrangements. Describe the arrangement even
                  if no payment was made during the year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if someone received payments because they left
                  their job, were terminated, or their employment changed due to
                  a change in organizational control (including settlements for
                  wrongful termination).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if a listed person participated in or received
                  payments from a special retirement plan meant only for
                  executives or highly paid employees, not available to all
                  staff.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if a listed person received or participated in
                  stock-type or ownership-based compensation (such as stock
                  options or similar arrangements).{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line asks whether the organization paid or promised
                  compensation to certain listed individuals that depends on how
                  much revenue is generated.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Indicate whether the organization paid compensation that was
                  based on the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    organization’s own revenue.
                  </span>{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Indicate whether the compensation was based on the{' '}
                  <span style={{ fontWeight: 600 }}>
                    revenue of a related organization
                  </span>
                  . If the answer is “Yes” to either line 5a or 5b, provide
                  additional details in Part III.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if a listed person’s pay or bonus was based on
                  the organization’s net earnings or profits. Describe the
                  details in Part III.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schj-part1-line7-img.webp" />
                  <source srcSet="/assets/images/f990-schj-part1-line7-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schj-part1-line7-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule J, Part I, lines 7-9, questions about compensation policies, approval process, and use of independent review."
                    width={760}
                    height={130}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if a listed person received non-fixed payments,
                  meaning payments where the amount or approval involved
                  discretion (not a set amount or fixed formula in a contract).
                  Example: Expense reimbursements or bonuses decided
                  case-by-case. Explain the arrangement in Part III.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if payments reported in Form 990, Part VII were
                  made under an initial written contract with someone who was
                  not a disqualified person before the contract started. (Fixed
                  payments under these contracts may qualify for a special
                  exception.) Describe the details in Part III.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Answer “Yes” if the initial contract in line 8 was properly
                  reviewed and approved using the organization’s formal approval
                  process (rebuttable presumption procedure).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Officers, Directors, Trustees, Key Employees, and
                  Highest Compensated Employees
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schj-part2-img.webp" />
                  <source srcSet="/assets/images/f990-schj-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schj-part2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule J – Part II: Compensation of officers, directors, trustees, key, and highest-paid employees."
                    width={760}
                    height={486}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Schedule J Part II requires detailed compensation information
                  for individuals for whom the organization answered “Yes” on
                  Form 990, Part IV, line 23.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  For each individual whose compensation must be reported on
                  Schedule J, report compensation from the organization on row
                  (i) and from related organizations, described in the
                  instructions, on row (ii). Do not list any individuals that
                  aren’t listed on Form 990, Part VII.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column(A)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Name and Title
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column(B)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Breakdown of{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2/"
                    sx={{ color: '#0202ff' }}
                  >
                    W-2
                  </Link>{' '}
                  and/or{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-misc/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-MISC
                  </Link>{' '}
                  and/or{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-nec/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-NEC
                  </Link>{' '}
                  Compensation
                </Typography>
                <Box
                  component="ol"
                  type="i"
                  sx={{ mb: 1, '& li': { mb: 1.2 } }}
                >
                  <li> Base Compensation</li>
                  <li>Bonus &amp; incentive compensation</li>
                  <li> Other reportable compensation </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column(C)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Retirement and other deferred compensation
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column(D)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Nontaxable benefits
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column(E)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Total of columns (B)(i) – (D)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column(F)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Compensation in column (B) reported as deferred on prior Form
                  990
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note</b> - All current key employees listed on Form 990,
                  Part VII, Section A must also be reported on Schedule J, Part
                  II, because their reportable compensation, by definition,
                  exceeds $150,000.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schj-part3-img.webp" />
                  <source srcSet="/assets/images/f990-schj-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schj-part3-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule J- Part III Details of Supplemental Information"
                    width={760}
                    height={281}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use Part III to provide narrative information, explanations,
                  or descriptions required for Part I, lines 1a, 1b, 3, 4a, 4b,
                  4c, 5a, 5b, 6a, 6b, 7, and 8, and for Part II.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Also, list here the name of each unrelated organization that
                  provided compensation to persons listed in Form 990, Part VII,
                  Section A; the type and amount of compensation the unrelated
                  organization paid or accrued; and the person receiving or
                  accruing such compensation.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule J filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Navigate the complexities of e-filing Form 990 with TaxZerone,
                  an IRS-authorized e-file service provider. Our intuitive
                  platform and dedicated support team will guide you seamlessly
                  through every step, ensuring a stress-free and efficient
                  filing experience.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here’s how your Form 990 return with Schedule J attachment is
                  transmitted to the IRS in 3 simple steps!
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Provide Organization Details -</b> Choose the tax year
                    for which you want to file a return, and provide your
                    organization’s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Preview the Return -</b> Complete Schedule J and preview
                    the information provided in the return for accuracy before
                    transmitting.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Transmit to the IRS -</b> Transmit Schedule J and your{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff' }}
                    >
                      990 return
                    </Link>{' '}
                    to the IRS and get the acceptance in just a few hours.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  With TaxZerone, you stay informed with instant updates on your
                  return’s filing status, eliminating guesswork and anxiety.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Even if the IRS rejects your{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990 return
                  </Link>
                  , our platform allows you to make necessary corrections and
                  resubmit it for FREE!
                </Typography>{' '}
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Simplify Your Tax Compliance Journey with TaxZerone
                      </Typography>
                      <Typography variant="body2">
                        Make the e-filing process simple by clicking the button
                        below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 260, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schj`}
                      >
                        Get Started With TaxZerone
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990sj.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule J (Form 990) – Compensation Information
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990 – Return of Organization Exempt From
                          Income Tax
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What type of compensation needs to be reported on Schedule
                  J?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Schedule J requires reporting all taxable and non-taxable
                    compensation provided to the covered employees. This
                    includes salaries, bonuses, commissions, non-cash benefits
                    like health insurance, employer-provided retirement
                    contributions, and deferred compensation provided to key
                    personnel and highly compensated employees.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. Which employees are considered &quot;key employees&quot;?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Key employees are individuals who have significant
                    responsibilities and influence within the organization,
                    typically including executive-level positions such as CEOs,
                    CFOs, and other senior management roles.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What if an employee receives compensation from a related
                  organization?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Compensation provided by related organizations (entities
                    under the control of the main organization) must also be
                    reported on Schedule J, alongside compensation from the
                    primary organization.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcJ;
